import { RootState } from '@/store/index';
import { ActionContext, ActionTree, Commit } from 'vuex';
import { AuthenticationState } from '@/store/modules/authentication';
import AuthenticationService from '@/services/authentication.service';
import { RequestPayload } from '@/models/base/RequestPayload';
import MockData from '@/globals/mock';
import {
  CHANGE_EXPIRED_PASSWORD,
  GENERATE_OTP,
  GET_OIDC_USER_MANAGER,
  LOGIN_BY_OTP,
  LOGIN_BY_PASSWORD,
  LOGIN_BY_PASSWORD_NO_INDEX,
  OIDC_SSO_LOGIN,
  RESET_PASSWORD,
  SEND_FORGOT_PASSWORD_MAIL,
  SET_AUTH_ERRORS,
  SET_AUTH_LOADING_MODE,
  SET_IS_RECAPTCHA_V2_USED,
  SET_LAST_SSO_USED_TOKEN,
  SET_OIDC_USER_MANAGER,
  SET_OTP_SENT,
  SET_SELECT_AUTH_METHOD_ERRORS,
  SET_SMS_SENDING,
  SET_USER_DETAILS,
  SSO_LOGIN,
} from '@/types/authentication.types';
import {
    AUTHORIZATION_ROOT,
    SET_TEMPORARY_TOKENS,
    SET_TOKENS,
    TEMPORARY_ACCESS_TOKEN,
} from '@/types/authorization.types';
import { accessTokenInterceptor, authErrorInterceptor } from '@/globals/http.interceptors';
import { LoginResponse } from '@/models/response/LoginResponse';
import { $httpClient } from '@/services/httpClient';
import { finalize } from 'rxjs/operators';
import { eventsHub } from '@/main';
import {
    LOBBY_ROOT,
    SET_INDEX_AND_OFFICES_LIST,
    SET_MUST_PERFORM_DATA_CLEANSING,
    SET_UPLOAD_FILE_ENABLE,
} from '@/types/lobby.types';
import { SmsSendingStatus } from '@/models/SmsSendingStatus';
import { sessionRecoveryInterceptor } from '@/globals/interceptors/session-recovery-interceptor';
import { SsoRequest } from '@/models/request/SsoRequest';
import { LoginRequest } from '@/models/request/LoginRequest';
import { OidcSsoRequest } from '@/models/request/OidcSsoRequest';

const RECAPTCHA_LOW_SCORE_ERROR_CODE = 74;

export const authenticationService = new AuthenticationService();
$httpClient.addRequestInterceptor(accessTokenInterceptor);
$httpClient.addResponseInterceptor(sessionRecoveryInterceptor);
$httpClient.addResponseInterceptor(authErrorInterceptor);

export const actions: ActionTree<AuthenticationState, RootState> = {

    [LOGIN_BY_PASSWORD]: (
        { commit, dispatch, state }: ActionContext<AuthenticationState, RootState>,
        requestBody: LoginRequest,
    ) => {
        authenticationService.loginByPassword(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                data => {
                    afterLogin(commit, data, requestBody);
                },
                error => {
                    if (error.indexAndOfficesList && error.indexAndOfficesList.length) {
                        commit(SET_INDEX_AND_OFFICES_LIST, error.indexAndOfficesList);
                    }
                    commit(SET_AUTH_ERRORS, error.messages || [ eventsHub.$t('login.network_error') ]);

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [LOGIN_BY_PASSWORD_NO_INDEX]: (
        { commit, dispatch, state }: ActionContext<AuthenticationState, RootState>,
        requestBody: LoginRequest,
    ) => {
        authenticationService.loginByPasswordNoIndex(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                data => {
                    afterLogin(commit, data, requestBody);
                },
                error => {
                    commit(SET_AUTH_ERRORS, error.messages || [ eventsHub.$t('login.network_error') ]);
                    if (error.indexAndOfficesList && error.indexAndOfficesList.length) {
                        commit(SET_INDEX_AND_OFFICES_LIST, error.indexAndOfficesList);
                    }

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [CHANGE_EXPIRED_PASSWORD]: (
        { commit, dispatch, state, rootGetters }: ActionContext<AuthenticationState, RootState>,
        changePasswordRequest: LoginRequest,
    ) => {
        const temporaryAccessToken = rootGetters[AUTHORIZATION_ROOT + TEMPORARY_ACCESS_TOKEN];
        authenticationService.changePassword(changePasswordRequest, temporaryAccessToken)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                data => {
                    eventsHub.$router.push({ name: 'password' });
                },
                error => {
                    commit(SET_AUTH_ERRORS, error.messages);

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [GENERATE_OTP]: (
        { commit, dispatch, state }: ActionContext<AuthenticationState, RootState>,
        loginRequest: LoginRequest,
    ) => {
        // commit(SET_SMS_SENDING, SmsSendingStatus.SENDING);
        authenticationService.generateOtp(loginRequest)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                () => {
                    commit(SET_OTP_SENT, true);
                    if (loginRequest.onSendAgain) {
                        commit(SET_SMS_SENDING, SmsSendingStatus.SENT);
                    }
                },
                error => {
                    if (loginRequest.onSendAgain) {
                        commit(SET_SMS_SENDING, SmsSendingStatus.FAILED);
                    }
                    commit(SET_AUTH_ERRORS, error.messages);

                    if (error.indexAndOfficesList && error.indexAndOfficesList.length) {
                        commit(SET_INDEX_AND_OFFICES_LIST, error.indexAndOfficesList);
                    }

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [LOGIN_BY_OTP]: (
        { commit, dispatch, state }: ActionContext<AuthenticationState, RootState>,
        requestBody: LoginRequest,
    ) => {
        authenticationService.loginByOtp(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                (data) => afterLogin(commit, data, requestBody),
                error => {
                    commit(SET_AUTH_ERRORS, error.messages);

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [SEND_FORGOT_PASSWORD_MAIL]: (
        { commit, dispatch, state }: ActionContext<AuthenticationState, RootState>,
        requestBody: LoginRequest,
    ) => {
        authenticationService.sendForgotPasswordMail(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                () => eventsHub.$router.push({ name: 'mail-sent' }),
                error => {
                    commit(SET_AUTH_ERRORS, error.messages);

                    if (error.indexAndOfficesList && error.indexAndOfficesList.length) {
                        commit(SET_INDEX_AND_OFFICES_LIST, error.indexAndOfficesList);
                    }

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [RESET_PASSWORD]: (
        { commit, dispatch, state }: ActionContext<AuthenticationState, RootState>,
        requestBody: LoginRequest,
    ) => {
        authenticationService.resetPassword(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                data => afterLogin(commit, data, requestBody),
                error => {
                    commit(SET_AUTH_ERRORS, error.messages);

                    if (error.errorCode === RECAPTCHA_LOW_SCORE_ERROR_CODE) {
                        commit(SET_IS_RECAPTCHA_V2_USED, true);
                    }
                },
            );
    },

    [SSO_LOGIN]: (
        { commit, state }: ActionContext<AuthenticationState, RootState>,
        { requestBody }: RequestPayload<SsoRequest>,
    ) => {
        if (state.authLoadingMode) {
            return;
        }

        commit(SET_LAST_SSO_USED_TOKEN, requestBody.ssoToken);
        commit(SET_AUTH_LOADING_MODE, true);
        authenticationService.ssoLogin(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                data => {
                    afterLogin(commit, data);
                },
                error => {
                    commit(SET_SELECT_AUTH_METHOD_ERRORS, error.messages || [ eventsHub.$t('login.network_error') ]);
                },
            );
    },


    [OIDC_SSO_LOGIN]: (
        { commit, state }: ActionContext<AuthenticationState, RootState>,
        { requestBody }: RequestPayload<OidcSsoRequest>,
    ) => {
        if (state.authLoadingMode) {
            return;
        }

        commit(SET_AUTH_LOADING_MODE, true);
        authenticationService.oidcSsoLogin(requestBody)
            .pipe(
                finalize(() => {
                        commit(SET_AUTH_LOADING_MODE, false);
                    },
                ),
            )
            .subscribe(
                data => {
                    afterLogin(commit, data);
                },
                error => {
                    commit(SET_SELECT_AUTH_METHOD_ERRORS, error.messages || [ eventsHub.$t('login.network_error') ]);
                },
            );
    },


  [GET_OIDC_USER_MANAGER]: (
    { commit, state }: ActionContext<AuthenticationState, RootState>,
    oidcId: string,
  ) => {
    commit(SET_AUTH_LOADING_MODE, true);

    authenticationService.getUserManager(oidcId)
      .pipe(
        finalize(() => {
            commit(SET_AUTH_LOADING_MODE, false);
          },
        ),
      )
      .subscribe(
        userManager => {
          commit(SET_OIDC_USER_MANAGER, userManager);
        },
        error => {
          commit(SET_SELECT_AUTH_METHOD_ERRORS, error.messages || [eventsHub.$t('oidc_error')]);
        },
      );
  },

};

const afterLogin = (commit: Commit, data: LoginResponse, requestBody?: any) => {
    data.userDetails.menu = MockData.mainMenu;
    commit(SET_USER_DETAILS, data.userDetails);
    commit(SET_UPLOAD_FILE_ENABLE, data.uploadFileEnable);
    if (data.passwordExpired) {
        commit(AUTHORIZATION_ROOT + SET_TEMPORARY_TOKENS, {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
        }, { root: true });
        eventsHub.$router.push({
            name: 'expired-password',
            params: {
                userId: data.userDetails.userId,
                indexNum: '' + data.userDetails.indexNum,
                officeNum: data.userDetails.officeNum ? ('' + data.userDetails.officeNum) : '',
                passwordExpired: 'true',
            },
        });
    } else if (data.mustPerformDataCleansing) {
        commit(AUTHORIZATION_ROOT + SET_TEMPORARY_TOKENS, {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
        }, { root: true });
        commit(LOBBY_ROOT + SET_MUST_PERFORM_DATA_CLEANSING,
            data.mustPerformDataCleansing, { root: true });
    } else {
        commit(AUTHORIZATION_ROOT + SET_TOKENS, {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
        }, { root: true });
        commit(SET_IS_RECAPTCHA_V2_USED, false);
        eventsHub.$router.push({ name: 'lobby' });
    }
};
